import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BaseUrl } from '../App';
 import { Circles } from 'react-loader-spinner';
 import { FadeLoader } from 'react-spinners';


const Toggle = ({ label, isOn, onToggle }) => (
  <div className="flex items-center justify-between">
    <span className="text-gray-700 text-sm">{label}</span>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        value=""
        className="sr-only peer"
        checked={isOn}
        onChange={onToggle}
      />
      <div className="group peer bg-white rounded-full duration-300 w-12 h-6 ring-2 ring-red-500 after:duration-300 after:bg-red-500 peer-checked:after:bg-green-500 peer-checked:ring-green-500 after:rounded-full after:absolute after:h-4 after:w-4 after:top-1 after:left-1 peer-checked:after:translate-x-6 peer-hover:after:scale-95"></div>
    </label>
  </div>
);

const EmailSettings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("")
 
  const [settings, setSettings] = useState({
    ct_admin_email_notification_status: 'N',
    ct_client_email_notification_status: 'N',
    ct_staff_email_notification_status: 'N',
    ct_admin_optional_email: '',
    ct_email_sender_name: '',
    ct_email_sender_address: '',
    ct_smtp_hostname: '',
    ct_smtp_username: '',
    ct_smtp_password: '',
    ct_smtp_port: '',
    ct_smtp_encryption: 'TLS',
    ct_smtp_authetication: 'true',
    ct_email_appointment_reminder_buffer: '60',
  });
  useEffect(() => {
    let timer;
    if (error) {
      timer = setTimeout(() => {
        setError("");
      }, 5000); // 5 seconds
    }
    return () => clearTimeout(timer);
  }, [error]);
  const fetchSettings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${BaseUrl}/backend/api/settings.php`, {
        action: 'get_all_settings',
        api_key: '1'
      });

      if (response.data.status === 'true' && response.data.statuscode === 200) {
        const settingsData = response.data.response;

        const getSettingValue = (name, defaultValue) => 
          settingsData.find(setting => setting.option_name === name)?.option_value || defaultValue;

        const newSettings = {
          ct_admin_email_notification_status: getSettingValue('ct_admin_email_notification_status', 'N'),
          ct_client_email_notification_status: getSettingValue('ct_client_email_notification_status', 'N'),
          ct_staff_email_notification_status: getSettingValue('ct_staff_email_notification_status', 'N'),
          ct_admin_optional_email: getSettingValue('ct_admin_optional_email', ''),
          ct_email_sender_name: getSettingValue('ct_email_sender_name', ''),
          ct_email_sender_address: getSettingValue('ct_email_sender_address', ''),
          ct_smtp_hostname: getSettingValue('ct_smtp_hostname', ''),
          ct_smtp_username: getSettingValue('ct_smtp_username', ''),
          ct_smtp_password: getSettingValue('ct_smtp_password', ''),
          ct_smtp_port: getSettingValue('ct_smtp_port', ''),
          ct_smtp_encryption: getSettingValue('ct_smtp_encryption', 'TLS'),
          ct_smtp_authetication: getSettingValue('ct_smtp_authetication', 'true'),
          ct_email_appointment_reminder_buffer: getSettingValue('ct_email_appointment_reminder_buffer', '60'),
        };

        setSettings(newSettings);
      } else {
        throw new Error(response.data.response || 'Failed to fetch settings');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setError(error.response?.data?.message || error.message || 'Failed to fetch settings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const updateSetting = async (optionName, optionValue) => {
    setLoading(true);
    try {
      const response = await axios.post(`${BaseUrl}/backend/api/settings.php`, {
        action: 'update_setting',
        api_key: '1',
        option_name: optionName,
        option_value: optionValue ?? '',
      });

      if (response.data.status === 'true' && response.data.statuscode === 200) {
        setSuccessMessage("Email Notification Update Successfully!")
        console.log(`Updated ${optionName} successfully`);
        return true;
      }
      throw new Error(response.data.response || `Failed to update ${optionName}`);
    } catch (error) {
      console.error(`Error updating ${optionName}:`, error);
      setError(error.response?.data?.message || error.message || `Failed to update ${optionName}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prevSettings => ({
      ...prevSettings,
      [name]: value,
    }));
  };

  const handleToggle = async (settingName) => {
    const newValue = settings[settingName] === 'Y' ? 'N' : 'Y';
    const updated = await updateSetting(settingName, newValue);
    if (updated) {
      setSettings(prevSettings => ({
        ...prevSettings,
        [settingName]: newValue,
      }));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    
    try {
      const updatePromises = Object.entries(settings).map(([name, value]) => 
        updateSetting(name, value)
      );

      const results = await Promise.all(updatePromises);
      
      if (results.every(result => result)) {
        await fetchSettings(); // Refresh settings after successful update
      } else {
        throw new Error('Some settings failed to update');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // if (isLoading) {
  //   return <div className="text-center">Loading...</div>;
  // }

  if (error) {
    return <div className="text-center text-red-500">{error}</div>;
  }

  return (
    <div className="relative min-h-screen">
    {/* Loading Indicator */}
      {loading && (
           <div className="fixed inset-0 flex items-center justify-center z-50">
             <div className="flex flex-col items-center p-3 bg-black border border-white rounded-2xl">
               <FadeLoader 
                 height={15}
                 width={5}
                 color="#ffffff"
                 ariaLabel="loading"
               />
               <h1 className="mt-1 text-lg text-white">Please wait...</h1>
             </div>
           </div>
         )}
         
    ) : (
    <div className="mx-auto mt-8 xl:px-72">
         {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      <div className="mb-8">
        <span className="text-gray-700 font-bold text-xl">Email Settings</span>
      </div>

      <div className="space-y-4">
        <Toggle
          label="Admin Email Notifications"
          isOn={settings.ct_admin_email_notification_status === 'Y'}
          onToggle={() => handleToggle('ct_admin_email_notification_status')}
        />
        <Toggle
          label="Client Email Notifications"
          isOn={settings.ct_client_email_notification_status === 'Y'}
          onToggle={() => handleToggle('ct_client_email_notification_status')}
        />
        <Toggle
          label="Staff Email Notification"
          isOn={settings.ct_staff_email_notification_status === 'Y'}
          onToggle={() => handleToggle('ct_staff_email_notification_status')}
        />

        <div>
          <label className="block text-gray-700 font-medium mb-1">
            Administrator Email
          </label>
          <input
            type="email"
            name="ct_admin_optional_email"
            value={settings.ct_admin_optional_email}
            onChange={handleInputChange}
            className="w-full border-gray-300 rounded px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="admin@example.com"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">
            Sender Name
          </label>
          <input
            type="text"
            name="ct_email_sender_name"
            value={settings.ct_email_sender_name}
            onChange={handleInputChange}
            className="w-full border-gray-300 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Sender Name"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">
            Sender Email
          </label>
          <input
            type="email"
            name="ct_email_sender_address"
            value={settings.ct_email_sender_address}
            onChange={handleInputChange}
            className="w-full border-gray-300 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="sender@example.com"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">
            SMTP Hostname
          </label>
          <input
            type="text"
            name="ct_smtp_hostname"
            value={settings.ct_smtp_hostname}
            onChange={handleInputChange}
            className="w-full border-gray-300 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="smtp.example.com"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">
            SMTP Username
          </label>
          <input
            type="text"
            name="ct_smtp_username"
            value={settings.ct_smtp_username}
            onChange={handleInputChange}
            className="w-full border-gray-300 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Username"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">
            SMTP Password
          </label>
          <input
            type="password"
            name="ct_smtp_password"
            value={settings.ct_smtp_password}
            onChange={handleInputChange}
            className="w-full border-gray-300 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Password"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">
            SMTP Port
          </label>
          <input
            type="number"
            name="ct_smtp_port"
            value={settings.ct_smtp_port}
            onChange={handleInputChange}
            className="w-full border-gray-300 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="587"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">
            SMTP Encryption
          </label>
          <select
            name="ct_smtp_encryption"
            value={settings.ct_smtp_encryption}
            onChange={handleInputChange}
            className="w-full border-gray-300 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="tls">TLS</option>
            <option value="ssl">SSL</option>
            <option value="plain">PLAIN</option>
          </select>
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">
            SMTP Authentication
          </label>
          <select
            name="ct_smtp_authetication"
            value={settings.ct_smtp_authetication}
            onChange={handleInputChange}
            className="w-full border-gray-300 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">
            Appointment Reminder Buffer
          </label>
          <select
            name="ct_email_appointment_reminder_buffer"
            value={settings.ct_email_appointment_reminder_buffer}
            onChange={handleInputChange}
            className="w-full border-gray-300 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="60">1 Hour</option>
            <option value="120">2 Hours</option>
            <option value="180">3 Hours</option>
            <option value="240">4 Hours</option>
            <option value="480">8 Hours</option>
            <option value="1440">1 Day</option>
          </select>
        </div>

        <div className="mt-6">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={`${
              isSaving ? 'bg-gray-400' : 'bg-blue-500'
            } text-white py-2 px-4 rounded focus:outline-none`}
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
    
  </div>
  );
};

export default EmailSettings;


